import { useMemo } from 'react';

import { formatPluralDuration } from '../../../../utils/durationUnit';
import { formatCurrencyCents } from '../../../../utils/formatters';
import { ContractStatus } from '../../../utils/userContract';
import { getOnboardingLinkFromContract } from '../../../utils/onboardingLink';
import texts from '../texts.json';
import { useBaseContracts } from './useBaseContracts';

export const DPL_OPTIONS_STATUS = {
  // No coach picked yet, so there is nothing to load.
  IDLE: 'idle',
  LOADING: 'loading',
  ERROR: 'error',
  // Loaded, but the coach has no base contract to link to.
  EMPTY: 'empty',
  READY: 'ready',
};

export const DPL_STATUS_NOTICE = {
  [DPL_OPTIONS_STATUS.IDLE]: { title: texts.sections.dpl.selectCoachFirst },
  [DPL_OPTIONS_STATUS.LOADING]: { title: texts.sections.dpl.loading },
  [DPL_OPTIONS_STATUS.ERROR]: { title: texts.sections.dpl.loadError },
  [DPL_OPTIONS_STATUS.EMPTY]: {
    title: texts.sections.dpl.emptyTitle,
    helper: texts.sections.dpl.emptyHelper,
  },
};

/**
 * A challenge is bought once for a fixed access window, so its stored total price is the
 * whole-term amount, not a monthly rate — appending `/mo` to it claims a subscription that
 * checkout never creates. Challenges read "$49 for 6 weeks"; every other contract keeps the
 * initial/monthly breakdown.
 */
const buildContractLabel = (contractDoc) => {
  const {
    name,
    initialPaymentInCentsStr,
    totalPriceInCentsStr,
    initialPaymentInCents,
    totalPriceInCents,
    currency,
    challengeId,
    initialTerm,
    durationUnit,
  } = contractDoc;
  const parts = [name || texts.sections.dpl.untitledContract];

  if (challengeId) {
    const priceInCents = initialPaymentInCents ?? totalPriceInCents;
    if (Number.isFinite(priceInCents)) {
      const priceStr = formatCurrencyCents(priceInCents, currency);
      parts.push(initialTerm
        ? `${priceStr} for ${formatPluralDuration(initialTerm, durationUnit)}`
        : priceStr);
    }
    return parts.join(' · ');
  }

  if (typeof initialPaymentInCentsStr === 'string' && initialPaymentInCentsStr) {
    parts.push(`${initialPaymentInCentsStr} initial`);
  }
  if (typeof totalPriceInCentsStr === 'string' && totalPriceInCentsStr) {
    parts.push(`${totalPriceInCentsStr}/mo`);
  }
  return parts.join(' · ');
};

const buildDplOption = (contractDoc) => ({
  contractId: contractDoc.id,
  url: getOnboardingLinkFromContract(contractDoc.id),
  name: contractDoc.name || texts.sections.dpl.untitledContract,
  label: buildContractLabel(contractDoc),
});

const resolveStatus = (coachId, { loading, error, options }) => {
  if (!coachId) return DPL_OPTIONS_STATUS.IDLE;
  if (error) return DPL_OPTIONS_STATUS.ERROR;
  if (loading) return DPL_OPTIONS_STATUS.LOADING;
  if (options.length === 0) return DPL_OPTIONS_STATUS.EMPTY;
  return DPL_OPTIONS_STATUS.READY;
};

const isPickable = (contractDoc) => contractDoc.status !== ContractStatus.CANCELED;

/**
 * The coach's base contracts as pickable DPL options, plus the one `status` that covers every state
 * a consumer has to render, so each consumer only varies the chrome it wraps them in.
 */
export const useDplOptions = (coachId) => {
  const { docs, loading, error } = useBaseContracts(coachId);
  const options = useMemo(() => docs.filter(isPickable).map(buildDplOption), [docs]);
  return { options, status: resolveStatus(coachId, { loading, error, options }) };
};
